<script lang="ts" setup>
  import { computed, useId, useSlots } from 'vue';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { FontIcon } from '../FontIcon';
  import { Button, ButtonVariant } from '../Button';
  import {
    containerVariantClass,
    descriptionClass,
    fullWidthClass,
    globalContainerClass,
    globalSectionClass,
    iconCircleClass,
    iconCircleVariantClass,
    inlineContainerClass,
    inlineSectionClass,
    innerContentBottomClass,
    innerContentRightClass,
    linkButtonOverrideClass,
    linkGroupClass,
    messageClass,
    textBoxClass,
  } from './FeedbackMessage.css';
  import type {
    FeedbackMessageProps,
    FeedbackMessageVariantType,
  } from './FeedbackMessage.types';
  import {
    FeedbackMessageActionPosition,
    FeedbackMessageMode,
    FeedbackMessageVariant,
  } from './FeedbackMessage.types';

  const feedbackMessageIconMap: Record<
    FeedbackMessageVariantType,
    TUNDR_ICONS
  > = {
    [FeedbackMessageVariant.INFO]: TUNDR_ICONS.INFO,
    [FeedbackMessageVariant.WARNING]: TUNDR_ICONS.ALERT,
    [FeedbackMessageVariant.ERROR]: TUNDR_ICONS.ERROR,
  };

  const props = withDefaults(defineProps<FeedbackMessageProps>(), {
    mode: FeedbackMessageMode.INLINE,
    actionPosition: FeedbackMessageActionPosition.RIGHT,
    fullWidth: false,
  });

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-feedback-message-${uid}`,
  );

  const slots = useSlots();

  const emit = defineEmits<{
    linkClick: [];
    buttonClick: [];
  }>();

  const isGlobal = computed(() => props.mode === FeedbackMessageMode.GLOBAL);

  const containerClasses = computed(() => {
    if (isGlobal.value) {
      return [globalContainerClass, containerVariantClass[props.variant]];
    }
    return [
      inlineContainerClass,
      containerVariantClass[props.variant],
      { [fullWidthClass]: props.fullWidth },
    ];
  });

  const sectionClasses = computed(() =>
    isGlobal.value ? globalSectionClass : inlineSectionClass,
  );

  const innerContentClasses = computed(() => {
    if (isGlobal.value) return textBoxClass;
    return props.actionPosition === FeedbackMessageActionPosition.BOTTOM
      ? innerContentBottomClass
      : innerContentRightClass;
  });

  const iconClasses = computed(() => [
    iconCircleClass,
    iconCircleVariantClass[props.variant],
  ]);

  const iconName = computed(() => feedbackMessageIconMap[props.variant]);

  const hasLink = computed(() => !!props.linkText);

  const hasButton = computed(() => !!props.buttonText);

  const ariaRole = computed(() =>
    props.variant === FeedbackMessageVariant.ERROR ? 'alert' : 'status',
  );

  const ariaLive = computed(() =>
    props.variant === FeedbackMessageVariant.ERROR ? 'assertive' : 'polite',
  );
</script>

<template>
  <div
    :class="containerClasses"
    :id="id"
    :role="ariaRole"
    :aria-live="ariaLive"
    :data-distinct-id="distinctId"
    :data-testid="resolvedTestId"
  >
    <div :class="sectionClasses">
      <slot name="icon">
        <div
          :class="iconClasses"
          aria-hidden="true"
          :data-testid="`${resolvedTestId}-icon`"
        >
          <FontIcon :icon-name="iconName" />
        </div>
      </slot>

      <div :class="innerContentClasses">
        <div :class="textBoxClass">
          <div :class="messageClass" :data-testid="`${resolvedTestId}-message`">
            <slot name="message">{{ message }}</slot>
          </div>
          <div
            v-if="slots.description || description"
            :class="descriptionClass"
            :data-testid="`${resolvedTestId}-description`"
          >
            <slot name="description">{{ description }}</slot>
          </div>
        </div>

        <slot v-if="!isGlobal" name="link">
          <span v-if="hasLink" :class="linkGroupClass">
            <Button
              :variant="ButtonVariant.Inline"
              :class="linkButtonOverrideClass"
              :test-id="`${resolvedTestId}-link`"
              @click="emit('linkClick')"
            >
              {{ linkText }}
            </Button>
            <FontIcon :icon-name="TUNDR_ICONS.CHEVRON_RIGHT" />
          </span>
        </slot>
      </div>
    </div>

    <slot v-if="isGlobal" name="button">
      <Button
        v-if="hasButton"
        :variant="ButtonVariant.Secondary"
        :test-id="`${resolvedTestId}-button`"
        @click="emit('buttonClick')"
      >
        {{ buttonText }}
      </Button>
    </slot>
  </div>
</template>
